import { Routes } from '@angular/router';

import { costAccessGuard } from './core/auth/cost-access.guard';
import { roleGuard } from './core/auth/role.guard';
import { landingRedirect } from './core/session/landing-redirect';
import { sessionGuard } from './core/session/session.guard';
import { CLIENT_BREADCRUMB_LABEL } from './pages/client-detail/client-breadcrumb-label';

/**
 * The frozen V1 route table (contracts/navigation-config.contract.md).
 * Every destination is lazily loaded via loadComponent (FR-008); every
 * protected destination carries [sessionGuard, roleGuard] in this order
 * (authentication before authorization, FR-010/FR-011; child routes inherit
 * via the grouping route's canActivateChild). /cost adds costClientRedirect
 * BETWEEN the two (AITH-47 D-01) — relocation is not authorization, and it
 * neither replaces nor weakens roleGuard. /sign-in and ** stay unprotected. data.breadcrumb declares each level's static label and,
 * for the nested client detail, its post-activation label source (FR-028).
 */
export const routes: Routes = [
  { path: '', redirectTo: landingRedirect, pathMatch: 'full' },
  {
    path: 'overview',
    loadComponent: () => import('./pages/overview/overview.page').then((m) => m.OverviewPage),
    canActivate: [sessionGuard, roleGuard],
    data: {
      breadcrumb: { label: 'Overview' },
      // GH-34 §1 (01-filters-decisions.md, D-3/D-5): two treatments, because they state two
      // different facts. `app`/`model`/`provider` are DISABLED — `rollup_daily` carries all three
      // (`RollupKey.RowKey`, `ProviderRollupKey`), so a rollup-first read could revive them and the
      // greyed control is a promise the platform can keep. `user`/`status` are HIDDEN — the rollup
      // stores users as one-way hashes that answer *how many* and never *which* (a recorded privacy
      // refusal), and carries no status dimension row at all, so neither is coming.
      //
      // AITH-45 U3's removal of these three is REVERSED here, not abandoned: its reasoning is about
      // obtainable option *values*, and a disabled control needs none. `client` joins the hidden set
      // instead — neither this page nor either cost operation accepts a client code, by design.
      //
      // GH-164 stage 3 (decisions D-01, D-06): `app` and `model` are now OPERABLE. The Overview reads
      // every filtered figure from the counter rows on every window, and declares the two widgets
      // that do not follow (Provider Mix, Active Users) in `narrowedWidgets`. Their option lists come
      // from the cross-client `filter-options` operation (D-04, D-12). `provider` stays Disabled: it
      // is stage 5, built only on a named request (D-08).
      //
      // GH-164 stage 5 (decisions D-08, owner 2026-10-31): no combination was requested, so
      // `provider` is reclassified HIDDEN rather than left as a greyed promise. A provider value
      // arriving on a shared link is still declared by the page's not-applicable note.
      filterBar: {
        hiddenDimensions: ['client', 'provider', 'user', 'status'],
      },
    },
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.page').then((m) => m.HomePage),
    canActivate: [sessionGuard, roleGuard],
    data: {
      breadcrumb: { label: 'Home' },
      // GH-164 stage 1 (aith-api#164, decisions D-02): all five are HIDDEN here. GH-34 §1 D-5 made
      // `app`/`model`/`provider` DISABLED, which promises the filter is coming to this page; on this
      // page it is not, so the promise is withdrawn rather than kept. Every Home figure comes from
      // the archive-backed semantic contracts (AITH-115) or `/dashboard/usage`, so filtering it means
      // a Synapse predicate, and its grids' rollup flags are still off in prod. A value arriving from
      // a shared link is still named by the page's not-applicable note, exactly as `user`/`status`
      // always were. Hiding changes presentation only: `FilterOptionsService` fetches the same lists.
      //
      // No `client` entry is needed: /home is projected to ClientAdmin and ClientUser only, so
      // `isInternalRole()` is false and the selector is already absent (SC-007).
      filterBar: {
        hiddenDimensions: ['app', 'model', 'provider', 'user', 'status'],
      },
    },
  },
  {
    // Component-less grouping route: the public URL space is unchanged and the
    // breadcrumb sees Clients as the ancestor of the client detail (FR-028).
    path: 'clients',
    canActivate: [sessionGuard, roleGuard],
    canActivateChild: [sessionGuard, roleGuard],
    data: { breadcrumb: { label: 'Clients' } },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/clients/clients.page').then((m) => m.ClientsPage),
        // AITH-46 U3, required by `filter-dimension-declarations.md`. GET /api/v1/clients is a
        // directory enumeration, not a telemetry read — it accepts no window, no client code and no
        // dimension — so a visible bar would be seven controls above a list none of them can touch.
        // Declared on THIS child and not on the `clients` parent: `FilterBarVisibilityService`
        // accumulates `hidden` down the tree and a child cannot undo it, so hoisting it would take
        // the bar off the detail page too, where every dimension is live.
        data: { filterBar: { hidden: true } },
      },
      {
        path: ':clientCode',
        loadComponent: () =>
          import('./pages/client-detail/client-detail.page').then((m) => m.ClientDetailPage),
        data: {
          breadcrumb: { label: 'Client', labelSource: CLIENT_BREADCRUMB_LABEL },
          // GH-164 stage 1 (aith-api#164, decisions D-02): all five are HIDDEN here. GH-34 §1 D-5 made
          // `app`/`model`/`provider` DISABLED, which promises the filter is coming to this page; on this
          // page it is not, so the promise is withdrawn rather than kept. The page already IS the
          // per-app, per-model and per-user breakdown, and most of it could not follow a page-wide
          // filter: the user grid never can from the rollup (`!uu` carries no app or model), the
          // activity feed would need a Synapse change, the model grid filtered by app loses its provider
          // and thinking-token columns, and the budget tile measures the whole budget. A page covered in
          // "this part does not follow your filter" notes is worse than no filter. A value arriving from
          // a shared link is still named by the page's not-applicable note, exactly as `user`/`status`
          // always were. Hiding changes presentation only: `FilterOptionsService` fetches the same lists.
          //
          // GH-34 §1 disabled rather than hid here because the Overview drills down INTO this page,
          // and App in full colour here beside App greyed there would break "grey means unavailable
          // HERE". That argument was against an operable control; a hidden one is absent, and "a
          // different page, different filters" is now the true reading. `client` is untouched: it is
          // Applied as navigation here and genuinely works.
          filterBar: {
            hiddenDimensions: ['app', 'model', 'provider', 'user', 'status'],
          },
        },
      },
    ],
  },
  {
    // AITH-46 D-02: the client-role view of the per-client detail page, on the SAME component.
    // No `:clientCode` segment by design — a client-role URL must never embed a caller-selected
    // tenant (AITH-43 FR-007), so the page resolves its client from the session instead.
    path: 'usage',
    loadComponent: () =>
      import('./pages/client-detail/client-detail.page').then((m) => m.ClientDetailPage),
    canActivate: [sessionGuard, roleGuard],
    data: {
      breadcrumb: { label: 'Usage' },
      // GH-164 stage 1 (aith-api#164, decisions D-02): the same component as /clients/:clientCode and
      // therefore the same treatment — all five HIDDEN, for the reasons stated there — declared
      // separately because these are two route entries. No `client` entry: the scope is
      // identity-derived on this route and the selector is already absent.
      filterBar: {
        hiddenDimensions: ['app', 'model', 'provider', 'user', 'status'],
      },
    },
  },
  {
    path: 'cost',
    loadComponent: () => import('./pages/cost/cost.page').then((m) => m.CostPage),
    // AITH-47 D-01: authentication, then RELOCATION, then authorization. This is one composite
    // guard because Angular starts guard-array members concurrently and roleGuard has telemetry.
    canActivate: [costAccessGuard],
    data: {
      breadcrumb: { label: 'Cost & Spending' },
      // GH-34 §1 (01-filters-decisions.md, D-3/D-5): two treatments, because they state two
      // different facts. `app`/`model`/`provider` are DISABLED — `rollup_daily` carries all three
      // (`RollupKey.RowKey`, `ProviderRollupKey`), so a rollup-first read could revive them and the
      // greyed control is a promise the platform can keep. `user`/`status` are HIDDEN — the rollup
      // stores users as one-way hashes that answer *how many* and never *which* (a recorded privacy
      // refusal), and carries no status dimension row at all, so neither is coming.
      //
      // Identical to /overview, and for the same reasons. AITH-47 D-07's own argument — neither cost
      // operation accepts a client code — is what puts `client` in the hidden set here.
      //
      // GH-164 stage 4 (decisions D-01, D-07): `model` and `provider` are now OPERABLE. Both grids and
      // the donut follow them, read from the rollup's model × provider rows. The page declares what
      // does not follow: budget alerts (a budget measures all spend), and the application count
      // under a provider filter. `app` stays Disabled: Cost App is stage 5, on request only (D-08).
      //
      // GH-164 stage 5 (decisions D-08, owner 2026-10-31): no combination was requested, so `app` is
      // reclassified HIDDEN. An application value arriving on a shared link is still declared by the
      // page's not-applicable note.
      filterBar: {
        hiddenDimensions: ['client', 'app', 'user', 'status'],
      },
    },
  },
  {
    path: 'exports',
    loadComponent: () => import('./pages/exports/exports.page').then((m) => m.ExportsPage),
    canActivate: [sessionGuard, roleGuard],
    data: {
      breadcrumb: { label: 'Exports' },
      // GH-34 §1 (01-filters-decisions.md, D-3/D-5): two treatments, because they state two
      // different facts. `app`/`model`/`provider` are DISABLED — `rollup_daily` carries all three
      // (`RollupKey.RowKey`, `ProviderRollupKey`), so a rollup-first read could revive them and the
      // greyed control is a promise the platform can keep. `user`/`status` are HIDDEN — the rollup
      // stores users as one-way hashes that answer *how many* and never *which* (a recorded privacy
      // refusal), and carries no status dimension row at all, so neither is coming.
      //
      // AITH-48 D-06 left this page alone on four reasons; GH-34 §1 D-6 finds three no longer hold,
      // and this is the page where an inert filter costs MOST. Everywhere else it shows unfiltered
      // numbers on a screen the reader is looking at; here it produces a file that leaves the
      // building. A staff member who sets App = billing-api, submits, and forwards the result has
      // every reason to believe the file is scoped to it — AITH-48's own contract says "a file is
      // every event for the client in the window". `dateRange` still pre-fills the request form and
      // `client` is still the export target: this page's own function is untouched.
      //
      // GH-164 stage 6 (decisions D-09, C-6): `app`, `model` and `provider` are now OPERABLE. A
      // submission carries them, the worker narrows its read to them, the form's declaration names
      // them, and each job row shows its own. Re-export and Retry replay the row's filter, never
      // the bar's. Nothing is Disabled here any more.
      filterBar: {
        hiddenDimensions: ['user', 'status'],
      },
    },
  },
  {
    path: 'configuration',
    loadComponent: () =>
      import('./pages/configuration/configuration.page').then((m) => m.ConfigurationPage),
    canActivate: [sessionGuard, roleGuard],
    data: {
      breadcrumb: { label: 'Configuration' },
      // AITH-80 U2, obligation 6: GET /api/v1/client-configurations is a directory enumeration with
      // a server-fixed month-to-date window — it accepts no window, no client code and no dimension
      // — so a visible bar would be seven controls above a table none of them can touch. The
      // absence IS the declaration; contracts/filter-dimension-declarations.md records all seven.
      filterBar: { hidden: true },
    },
  },
  {
    path: 'sign-in',
    loadComponent: () => import('./pages/sign-in/sign-in.page').then((m) => m.SignInPage),
    // AITH-44 R-011: the filter bar defaults to SHOWN; these two destinations
    // are the explicit opt-outs. An opt-in default would silently drop the bar
    // from AITH-45–48's pages.
    data: {
      breadcrumb: { label: 'Sign in' },
      filterBar: { hidden: true },
      shellChrome: { hidden: true },
    },
  },
  {
    path: '**',
    loadComponent: () => import('./pages/not-found/not-found.page').then((m) => m.NotFoundPage),
    data: {
      breadcrumb: { label: 'Page not found' },
      filterBar: { hidden: true },
      shellChrome: { hidden: true },
    },
  },
];
